import type { PlayerIndex } from '@md/shared';
import { COLLECTIBLE_SIZE_TILES, CollectibleDefSchema, type EntityDef } from '@md/shared';
import { aabbOverlaps } from '../physics/aabb.js';
import { bodyAabb } from '../physics/body.js';
import {
  createEntity,
  defaultEntityId,
  ENTITY_PHASE_SENSOR,
  type EntityInstance,
  entityAabb,
  matchesPlayer,
} from './entity.js';

/** Options de fabrication d'un collectible, pour les variantes des jeux. */
export interface CollectibleOptions {
  readonly type?: string;
  readonly player?: PlayerIndex;
  readonly tint?: number;
}

/**
 * Collectible générique (les « boulons » de Magnetic Duo, concept §4.2).
 *
 * Ramassé au premier recouvrement avec le joueur autorisé ; `flags.collected`
 * passe à 1 et l'entité cesse d'être dessinée. Le décompte total/collecté est
 * assemblé par la simulation, qui en déduit la 2ᵉ étoile.
 */
export function createCollectible(
  def: EntityDef,
  options: CollectibleOptions = {},
): EntityInstance {
  const cfg = CollectibleDefSchema.parse({ ...def, type: 'collectible' });
  const owner = options.player ?? cfg.player;
  const type = options.type ?? 'collectible';
  const margin = (1 - COLLECTIBLE_SIZE_TILES) / 2;

  return createEntity({
    id: cfg.id ?? defaultEntityId(type, cfg.x, cfg.y),
    type,
    x: cfg.x + margin,
    y: cfg.y + margin,
    w: COLLECTIBLE_SIZE_TILES,
    h: COLLECTIBLE_SIZE_TILES,
    phase: ENTITY_PHASE_SENSOR,
    flags: { collected: 0, collectible: 1, player: owner ?? -1 },
    appearance: { shape: 'gem', tint: options.tint ?? 0xffd166, activeTint: 0xffffff },
    update(self, ctx) {
      if (self.flags.collected === 1) {
        return;
      }
      const box = entityAabb(self);
      for (const player of ctx.players) {
        if (player.dead || !matchesPlayer(player.index, owner)) {
          continue;
        }
        if (aabbOverlaps(bodyAabb(player.body), box)) {
          self.flags.collected = 1;
          return;
        }
      }
    },
  });
}
